import { useCallback, useState } from "react";

import { submit } from "../../lib/runtime";
import { useCanvasState } from "../../lib/useCanvasState";
import {
  boxesOf,
  ICON_LABEL,
  phaseOf,
  powersOf,
  powersOn,
  votingIsOpen,
  type Icon,
  type Power,
} from "./show";

const TINT: Record<Icon, string> = {
  buff: "#ffcf5c",
  debuff: "#5cd6ff",
  attack: "#ff6b8b",
};

const GLYPH: Record<Icon, string> = { buff: "^", debuff: "v", attack: "Z" };

const SAYS: Record<string, string> = {
  idle: "Waiting for the host to start a round.",
  voting: "The question is still hidden. Set the board up now.",
  asking: "The question is up. You can still act, until somebody buzzes.",
  answering: "Somebody buzzed. Voting is closed until the next round.",
};

/** Every option the crowd may vote for, one button each, so no combination is hidden. */
export function ShowViewer({ voteMount, stageMount }: { voteMount: string; stageMount: string }) {
  const stage = useCanvasState(stageMount);
  const taking = useCanvasState(voteMount);
  const [refused, setRefused] = useState<string | null>(null);

  const vote = useCallback(
    (option: string) => {
      setRefused(null);
      submit(voteMount, "pick", option).catch((error: unknown) => {
        setRefused(error instanceof Error ? error.message : "that was refused");
      });
    },
    [voteMount],
  );

  const boxes = boxesOf(stage.state);
  const powers = powersOf(stage.state);
  const phase = phaseOf(stage.state);
  const open = votingIsOpen(taking.state);

  return (
    <section style={page}>
      <header>
        <h1 style={{ margin: 0, fontSize: 22 }}>Pick a power</h1>
        <p style={{ margin: "8px 0 0", opacity: 0.75 }}>{SAYS[phase]}</p>
        <p style={{ ...banner, background: open ? "rgba(91,229,143,0.16)" : "rgba(255,255,255,0.06)" }}>
          {open ? "Voting is open" : "Voting is closed"}
        </p>
      </header>

      {boxes.map((box) => (
        <section key={box.place} style={card}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12 }}>
            <strong>{box.label}</strong>
            <span style={{ flex: 1, opacity: 0.65 }}>
              {box.taken ? (box.name === "" ? "unnamed" : `box ${String(box.place)}`) : "nobody in it"}
            </span>
            <strong>{Math.round(box.points)}</strong>
          </div>

          <div style={quad}>
            {powersOn(powers, box.place).map((power) => (
              <Option
                key={power.id}
                power={power}
                robbing={boxes.find((one) => one.place === power.targetPlace)?.label ?? ""}
                disabled={!open || power.cooling || !box.taken}
                onPick={() => vote(power.id)}
              />
            ))}
          </div>
        </section>
      ))}

      {refused !== null && <p style={{ color: "#ff8f8f", marginTop: 16 }}>{refused}</p>}
    </section>
  );
}

/** One votable option. A steal names the box it robs, so every combination is its own button. */
function Option({
  power,
  robbing,
  disabled,
  onPick,
}: {
  power: Power;
  robbing: string;
  disabled: boolean;
  onPick: () => void;
}) {
  const tint = TINT[power.icon];
  const label =
    power.targetPlace > 0
      ? `${ICON_LABEL[power.icon]} ${robbing === "" ? String(power.targetPlace) : robbing}`
      : ICON_LABEL[power.icon];
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPick}
      style={{
        ...pill,
        borderColor: disabled ? "rgba(255,255,255,0.12)" : tint,
        color: disabled ? "rgba(244,247,255,0.45)" : tint,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      <span style={{ fontSize: 18, fontWeight: 800 }}>{GLYPH[power.icon]}</span>
      <span style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>{label}</span>
      {power.cooling && <span style={{ fontSize: 10, opacity: 0.8 }}>cooling</span>}
      {power.tally > 0 && (
        <span style={{ ...tally, background: tint }}>{Math.round(power.tally)}</span>
      )}
    </button>
  );
}

const page: React.CSSProperties = {
  display: "block",
  maxWidth: 520,
  margin: "0 auto",
  padding: "32px 20px 60px",
  fontFamily: "system-ui, sans-serif",
};

const banner: React.CSSProperties = {
  margin: "12px 0 0",
  padding: "8px 12px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 700,
};

const card: React.CSSProperties = {
  marginTop: 18,
  padding: 16,
  borderRadius: 12,
  background: "rgba(255,255,255,0.05)",
};

const quad: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 8,
};

const pill: React.CSSProperties = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 3,
  padding: "10px 6px",
  background: "transparent",
  border: "1px solid",
  borderRadius: 12,
};

const tally: React.CSSProperties = {
  position: "absolute",
  top: -8,
  right: -6,
  minWidth: 22,
  padding: "2px 6px",
  borderRadius: 11,
  fontSize: 12,
  fontWeight: 800,
  color: "#12162a",
};
